import { useState, useEffect, FC } from 'react';
import { GridCellParams, GridColDef, GridToolbar } from '@mui/x-data-grid';
import { toast } from '@cincoders/cinnamon';
import { ProcessGrid, TableDiv } from './styles';
import { renderProgress } from '../ProgressBarProcessStatus';
import { ForecastProps, renderForecast } from '../Forecast/Forecast';
import { ProcessesStatisticsService } from '../../services/ProcessesStatisticsService';
import { ProcessStatisticsDTO } from '../../types/ProcessStatistics.d';
import { DivStar } from '../Favourite/index';
import { ProcessService } from '../../services/ProcessService';

interface Process {
  fav: boolean;
  id: number;
  number: string;
  name: string;
  type: string;
  generalProgress: number;
  currentUnit: string;
  unitProgress: number;
  forecast: ForecastProps;
}

const Table: FC = () => {
  const [screenSize, setScreenSize] = useState<string>();
  const [rows, setRows] = useState<Process[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [pageState, setPageState] = useState({
    page: 1,
    total: 0,
    isLoading: false,
    pageSize: 10,
  });
  const updateFavourite = (params: GridCellParams) => {
    let tempProcess: Process = {
      fav: false,
      id: 0,
      number: '',
      name: '',
      type: '',
      generalProgress: 0,
      currentUnit: '',
      unitProgress: 0,
      forecast: {
        date: '',
        delayedDays: 0,
      },
    };
    const i = rows.map(x => x.id).indexOf(params.row.id);
    tempProcess = rows[i];
    try {
      ProcessService.favouriteProcess(tempProcess.id, !tempProcess.fav);
      if (tempProcess.fav) {
        tempProcess.fav = false;
      } else {
        tempProcess.fav = true;
      }
      rows[i].fav = tempProcess.fav;
    } catch {
      toast.error('Não foi possível favoritar. Tente novamente mais tarde.');
    }
  };
  const columns: GridColDef[] = [
    {
      field: 'favourite',
      headerName: screenSize !== 'small' ? 'FAVORITO' : 'FAV.',
      flex: screenSize !== 'small' ? 2 : 1.5,
      minWidth: 80,
      maxWidth: 120,
      renderCell: params => <DivStar fav={params.row.fav} onChange={() => updateFavourite(params)} />,
    },
    {
      field: 'number',
      headerName: screenSize !== 'small' ? 'N° DO PROCESSO' : 'Nº PROCESSO',
      flex: screenSize !== 'small' ? 3 : 2.75,
      minWidth: 145,
      maxWidth: 210,
    },
    {
      field: 'name',
      headerName: 'NOME',
      flex: screenSize !== 'small' ? 3.5 : 4,
      minWidth: 80,
    },
    { field: 'type', headerName: 'TIPO', flex: 2, minWidth: 80, maxWidth: 150 },
    {
      field: 'generalProgress',
      headerName: screenSize !== 'large' ? 'PROG. GERAL' : 'PROGRESSO GERAL',
      renderCell: renderProgress,
      flex: screenSize !== 'small' ? 2 : 1.25,
      minWidth: 150,
    },
    {
      field: 'currentUnit',
      headerName: 'UNIDADE ATUAL',
      flex: screenSize !== 'small' ? 2.4 : 1.7,
      minWidth: 140,
    },
    {
      field: 'unitProgress',
      headerName: screenSize !== 'large' ? 'PROG. UNIDADE' : 'PROGRESSO UNIDADE',
      renderCell: renderProgress,
      flex: screenSize !== 'small' ? 2 : 1.25,
      minWidth: 150,
    },
    {
      field: 'forecast',
      headerName: screenSize !== 'large' ? 'PREV. TÉRMINO' : 'PREVISÃO DE TÉRMINO',
      renderCell: renderForecast,
      flex: screenSize !== 'small' ? 2.5 : 3.5,
      minWidth: 100,
      maxWidth: 200,
    },
  ];

  function handleWindowResize() {
    if (window.innerWidth <= 1366 && window.innerWidth > 1060) {
      if (screenSize !== 'medium') {
        setScreenSize('medium');
      }
    } else if (window.innerWidth <= 1060 && window.innerWidth >= 650) {
      if (screenSize !== 'small') {
        setScreenSize('small');
      }
    } else if (window.innerWidth < 650) {
      if (screenSize !== 'small') {
        setScreenSize('small');
      }
    } else {
      setScreenSize('large');
    }
  }

  useEffect(() => {
    handleWindowResize();
    window.addEventListener('resize', handleWindowResize, false);
  }, []);

  useEffect(() => {
    processesStatistics(pageState.page, pageState.pageSize);
  }, [pageState.pageSize, pageState.page]);

  async function processesStatistics(page: number, pageSize: number) {
    setRows([]);
    setLoading(true);
    try {
      const response = await ProcessesStatisticsService.getProcessesStatistics(pageSize, page - 1);
      const processDTOList: ProcessStatisticsDTO[] = response.data.data;
      const dateTime = new Intl.DateTimeFormat('pt-BR');
      setPageState({ ...pageState, total: response.data.totalElements });

      const processes: Process[] = processDTOList.map(processDTO => {
        const currentPrevisionDate = dateTime.format(new Date(processDTO.currentPrevisionDate));

        const delayedDays =
          (Date.parse(processDTO.currentPrevisionDate) - Date.parse(processDTO.initialPrevisionDate)) /
          (1000 * 60 * 60 * 24);

        const forecast: ForecastProps = {
          date: currentPrevisionDate,
          delayedDays: Math.floor(delayedDays),
        };
        return {
          id: processDTO.processId,
          sipacId: processDTO.sipacId,
          fav: processDTO.processFavourite,
          number: processDTO.number,
          name: processDTO.name,
          type: processDTO.type,
          generalProgress: processDTO.progress,
          currentUnit: processDTO.currentUnit,
          unitProgress: processDTO.currentUnitProgress,
          forecast,
        };
      });

      setRows(processes);
    } catch {
      toast.error('Não foi possível carregar processos. Tente novamente mais tarde.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <TableDiv>
      <ProcessGrid
        rows={rows}
        columns={columns}
        loading={loading}
        rowCount={pageState.total}
        rowHeight={70}
        onPageChange={(newPage: number) => {
          setPageState({ ...pageState, page: newPage + 1 });
        }}
        onPageSizeChange={newPageSize => {
          setPageState({ ...pageState, pageSize: newPageSize });
        }}
        page={pageState.page - 1}
        paginationMode='server'
        pageSize={pageState.pageSize}
        rowsPerPageOptions={[5, 10, 20]}
        components={{
          Toolbar: GridToolbar,
        }}
        sx={{
          '&>.MuiDataGrid-main': {
            '&>.MuiDataGrid-columnHeaders': {
              outline: 0,
            },
            '& div div div div >.MuiDataGrid-cell': {
              outline: 0,
            },
          },
        }}
      />
    </TableDiv>
  );
};

export default Table;
